/* Web Push client helpers */

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getPermission = () => (isPushSupported() ? Notification.permission : 'unsupported');

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
};

const registerSW = async () => {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
};

const fetchPublicKey = async () => {
  const res = await fetch(`${API_URL}/api/push/public-key`);
  const data = await res.json();
  return data.publicKey;
};

export const getLocalTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
};

const keysMatch = (sub, publicKey) => {
  try {
    const cur = sub?.options?.applicationServerKey;
    if (!cur) return true;
    const a = new Uint8Array(cur); const b = urlBase64ToUint8Array(publicKey);
    return a.length === b.length && a.every((v, i) => v === b[i]);
  } catch { return true; }
};

// Permission must be requested synchronously inside the tap handler (Safari/iOS requirement),
// so this call is the FIRST await in the click path — don't move network calls ahead of it.
export const subscribeToPush = async () => {
  if (!isPushSupported()) throw new Error('Push not supported on this browser');

  const permission = await new Promise((resolve) => {
    try {
      const r = Notification.requestPermission(resolve);
      if (r && typeof r.then === 'function') r.then(resolve);
    } catch { resolve(Notification.permission); }
  });
  if (permission !== 'granted') throw new Error('Permission denied');

  const registration = await registerSW();
  await navigator.serviceWorker.ready;

  const publicKey = await fetchPublicKey();
  if (!publicKey) throw new Error('Server is missing VAPID public key');
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !keysMatch(subscription, publicKey)) {
    // Server key rotated since this device subscribed — start fresh.
    try { await subscription.unsubscribe(); } catch { /* ignore */ }
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const token = localStorage.getItem('rbr_token');
  const res = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      timezone: getLocalTimezone(),
    }),
  });
  if (!res.ok) throw new Error('Server could not save subscription');
  return subscription;
};

export const unsubscribeFromPush = async () => {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return;
  const sub = await registration.pushManager.getSubscription();
  const endpoint = sub?.endpoint;
  if (sub) await sub.unsubscribe();

  const token = localStorage.getItem('rbr_token');
  await fetch(`${API_URL}/api/push/unsubscribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
};

export const hasActiveSubscription = async () => {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return false;
  const sub = await registration.pushManager.getSubscription();
  return !!sub;
};

export const sendTestPush = async () => {
  const token = localStorage.getItem('rbr_token');
  const res = await fetch(`${API_URL}/api/push/test`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.ok;
};
